import { useMemo, type CSSProperties, type FC, type ReactNode } from 'react';
import Popup from 'reactjs-popup';
import type { PopupPosition, PopupProps } from 'reactjs-popup/dist/types';
import { observer } from 'mobx-react';
import './Popover.scss';

export interface PopoverProps
  extends Omit<PopupProps, 'children' | 'className'> {
  /** Element the popover anchors to and (with `on`) toggles from. */
  trigger: PopupProps['trigger'];
  /** Popover body, or a render function receiving the imperative `close`. */
  children: ReactNode | ((close: () => void) => ReactNode);
  /** Show the little arrow pointing at the trigger. Off by default. */
  arrow?: boolean;
  /** Render the overlay without the grey dim + backdrop blur from `.popup-overlay`. */
  withoutBackdrop?: boolean;
  className?: string;
  contentStyle?: CSSProperties;
  overlayStyle?: CSSProperties;
}

/**
 * Anchored, interactive popover built on `reactjs-popup`. Sibling to `<Tooltip>`:
 * Tooltip is for passive text labels, `<Popover>` is for an interactive panel
 * (forms, confirm dialogs, menus) anchored to a trigger via `position`/`on`.
 *
 * For centered/dimmed app-blocking dialogs use `<Modal>` instead — `<Popover>` is
 * always trigger-anchored and never owns a full-screen backdrop.
 *
 * Standardizes the popover defaults that were duplicated across raw `<Popup>` usages
 * (content z-index + soft shadow, arrow off, close-on-outside). Everything else passes
 * straight through to reactjs-popup.
 */
const Popover: FC<PopoverProps> = observer(
  ({
    trigger,
    children,
    arrow = false,
    withoutBackdrop = false,
    className,
    closeOnDocumentClick = true,
    contentStyle,
    overlayStyle,
    position,
    ...rest
  }) => {
    const newPosition = useMemo(() => {
      const initialPosition: PopupPosition[] = ['bottom right', 'bottom left'];
      if (!position) {
        return initialPosition;
      }
      if (typeof position === 'string') {
        return [position, ...initialPosition];
      }
      return [...position, ...initialPosition];
    }, [position]);
    return (
      <Popup
        trigger={trigger}
        arrow={arrow}
        closeOnDocumentClick={closeOnDocumentClick}
        className={className}
        keepTooltipInside='html'
        contentStyle={{
          zIndex: 'var(--z-popover-content)',
          boxShadow:
            '0px 0px 2px 0px rgba(152, 162, 179, 0.15), 0px 0px 12px 0px rgba(152, 162, 179, 0.15)',
          ...contentStyle
        }}
        overlayStyle={{
          zIndex: 'var(--z-popover-overlay)',
          ...(withoutBackdrop && {
            backgroundColor: 'transparent',
            backdropFilter: 'none'
          }),
          ...overlayStyle
        }}
        {...rest}
        position={newPosition}
      >
        {children as PopupProps['children']}
      </Popup>
    );
  }
);

export default Popover;
